/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

import React from "react";
import { Checkbox } from "@mui/material";
import { DeviceDetailsProps, AlarmDefinition } from "../types";
import AlarmTable from "../components/AlarmTable";
import DetailRow from "../components/DetailRow";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { getAlarmLedColor } from "../utils";

const PsuDeviceDetails: React.FC<DeviceDetailsProps> = ({
  device,
  parsedAttributes,
  statusLabel,
  alarmsOnly = false,
}) => {
  const isDisconnected = statusLabel === "Disconnected";

  const normalizeAttrKey = (value: string) => value.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
  const getAttrValueInsensitive = (key: string) => {
    const direct = parsedAttributes[key];
    if (direct !== undefined) return direct;

    const lowerKey = key.toLowerCase();
    const matchedKey = Object.keys(parsedAttributes).find((k) => k.toLowerCase() === lowerKey);
    if (matchedKey) return parsedAttributes[matchedKey];

    const normalizedKey = normalizeAttrKey(key);
    const matchedNormalizedKey = Object.keys(parsedAttributes).find(
      (k) => normalizeAttrKey(k) === normalizedKey
    );
    return matchedNormalizedKey ? parsedAttributes[matchedNormalizedKey] : undefined;
  };

  const getArrayAttr = (keys: string[]) => {
    for (const key of keys) {
      const value = getAttrValueInsensitive(key);
      if (Array.isArray(value)) {
        return value;
      }
    }

    return [] as any[];
  };

  const psuEquipmentModel = getAttrValueInsensitive("psuEquipmentModel") || device.model;
  const psuEquipmentSn = getAttrValueInsensitive("psuEquipmentSN") || device.serialNumber;
  const psuSiteInfo = getAttrValueInsensitive("psuSiteInfo");
  const psuTemperature = getAttrValueInsensitive("psuTemperature");
  const psuFirmwareVersion = getAttrValueInsensitive("psuFirmwareVersion");
  const psuFanSpeed = getAttrValueInsensitive("psuFanSpeed");
  const psuInputVoltage = getAttrValueInsensitive("psuInputVoltage");
  const psuOutputVoltage = getAttrValueInsensitive("psuOutputVoltage");
  const psuPowerConsumption = getAttrValueInsensitive("psuPowerConsumption");

  const hasDeviceInfo =
    psuEquipmentModel ||
    psuEquipmentSn ||
    psuSiteInfo ||
    psuTemperature !== undefined ||
    psuFirmwareVersion ||
    psuFanSpeed !== undefined ||
    psuInputVoltage !== undefined ||
    psuOutputVoltage !== undefined ||
    psuPowerConsumption !== undefined;

  const psuAlarms = getAttrValueInsensitive("psuAlarms");
  const psuAlarmsMask = getAttrValueInsensitive("psuAlarmsMask");
  const hasPsuAlarms = psuAlarms !== undefined || psuAlarmsMask !== undefined;

  const psuFanAlarm = getArrayAttr(["psuFanAlram", "psuFanAlarm"]);
  const psuPowerAlarm = getArrayAttr(["psuPowerAlram", "psuPowerAlarm"]);

  const sortByNumber = (items: any[], key: string) =>
    [...items].sort((a, b) => Number(a?.[key]) - Number(b?.[key]));

  const psuFanAlarmSorted = sortByNumber(psuFanAlarm, "psuFanNumber");
  const psuPowerAlarmSorted = sortByNumber(psuPowerAlarm, "psuChannelNumber");

  const hasPsuFanAlarm = psuFanAlarmSorted.length > 0;
  const hasPsuPowerAlarm = psuPowerAlarmSorted.length > 0;

  const psuAlarmDefinitions: AlarmDefinition[] = [
    { bit: 1, name: "Overall Alarm" },
    { bit: 2, name: "Output Over Voltage Alarm" },
    { bit: 3, name: "Input Under Voltage Alarm" },
    { bit: 4, name: "Over Temperature Alarm" },
    { bit: 5, name: "Version Mismatch Alarm" },
  ];

  const renderStatusAlarmCard = (
    title: string,
    items: any[],
    numberKey: string,
    alarmKey: string,
    maskKey: string,
    labelPrefix: string
  ) => {
    if (items.length === 0) return null;

    return (
      <Card className="mb-2 overflow-hidden">
        <CardHeader className="pb-3 px-4 pt-4">
          <CardTitle className="text-sm">{title}</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {items.map((item: any, index: number) => {
              const itemNumber = item?.[numberKey] ?? index + 1;
              const isAlarmActive = Number(item?.[alarmKey]) > 0;
              const isMaskEnabled = Number(item?.[maskKey]) > 0;

              return (
                <div
                  key={`${title}-${itemNumber}-${index}`}
                  className="rounded-xl border p-4 shadow-sm"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold">
                      {labelPrefix} {itemNumber}
                    </div>

                    <div
                      className="w-3 h-3 rounded-full"
                      style={{
                        backgroundColor: getAlarmLedColor(isDisconnected, isAlarmActive),
                      }}
                    />
                  </div>

                  <div className="mt-3 text-xs flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">
                      Status:{" "}
                      <span className="font-medium text-foreground">
                        {isAlarmActive ? "Alarm Active" : "Normal"}
                      </span>
                    </span>
                    <Checkbox
                      checked={isMaskEnabled}
                      disabled
                      size="small"
                      sx={{
                        padding: 0,
                        transform: "scale(0.7)",
                        color: "var(--foreground)",
                        "&.Mui-disabled": {
                          color: "var(--muted-foreground)",
                        },
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (alarmsOnly) {
    if (!hasPsuAlarms && !hasPsuFanAlarm && !hasPsuPowerAlarm) return null;

    return (
      <>
        {hasPsuAlarms && (
          <Card className="mb-2 overflow-hidden">
            <CardHeader className="pb-3 px-4 pt-4">
              <CardTitle className="text-sm">PSU Alarms</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <AlarmTable
                alarms={psuAlarms}
                alarmsMask={psuAlarmsMask}
                alarmDefinitions={psuAlarmDefinitions}
                isDisconnected={isDisconnected}
              />
            </CardContent>
          </Card>
        )}

        {renderStatusAlarmCard(
          "PSU Fan Alarm",
          psuFanAlarmSorted,
          "psuFanNumber",
          "psuFanAlarm",
          "psuFanAlarmMask",
          "Fan"
        )}

        {renderStatusAlarmCard(
          "PSU Power Alarm",
          psuPowerAlarmSorted,
          "psuChannelNumber",
          "psuOverPowerConsumptionAlarm",
          "psuOverPowerConsumptionAlarmMask",
          "Channel"
        )}
      </>
    );
  }

  if (!hasDeviceInfo && !hasPsuAlarms && !hasPsuFanAlarm && !hasPsuPowerAlarm) return null;

  return (
    <>
      {hasDeviceInfo && (
        <Card className="mb-2">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">Device Information</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 pt-0">
            {psuSiteInfo && <DetailRow label="Site" value={psuSiteInfo} />}
            {psuEquipmentModel && <DetailRow label="Model" value={psuEquipmentModel} />}
            {psuEquipmentSn && <DetailRow label="Serial Number" value={psuEquipmentSn} />}
            {psuTemperature !== undefined && <DetailRow label="Temperature" value={`${psuTemperature}°C`} />}
            {psuFirmwareVersion && <DetailRow label="Firmware Version" value={psuFirmwareVersion} />}
            {psuFanSpeed !== undefined && <DetailRow label="Fan Speed" value={`${psuFanSpeed}`} />}
            {psuInputVoltage !== undefined && <DetailRow label="Input Voltage" value={`${psuInputVoltage}`} />}
            {psuOutputVoltage !== undefined && <DetailRow label="Output Voltage" value={`${psuOutputVoltage}`} />}
            {psuPowerConsumption !== undefined && <DetailRow label="Power Consumption" value={`${psuPowerConsumption}`} />}
          </CardContent>
        </Card>
      )}

      {hasPsuAlarms && (
        <Card className="mb-2 overflow-hidden">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">PSU Alarms</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <AlarmTable
              alarms={psuAlarms}
              alarmsMask={psuAlarmsMask}
              alarmDefinitions={psuAlarmDefinitions}
              isDisconnected={isDisconnected}
            />
          </CardContent>
        </Card>
      )}

      {renderStatusAlarmCard(
        "PSU Fan Alarm",
        psuFanAlarmSorted,
        "psuFanNumber",
        "psuFanAlarm",
        "psuFanAlarmMask",
        "Fan"
      )}

      {renderStatusAlarmCard(
        "PSU Power Alarm",
        psuPowerAlarmSorted,
        "psuChannelNumber",
        "psuOverPowerConsumptionAlarm",
        "psuOverPowerConsumptionAlarmMask",
        "Channel"
      )}

    </>
  );
};

export default PsuDeviceDetails;
